import { Fragment, useMemo, useState } from 'react';

import { ChevronDown, ChevronRight, Copy, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreateWhatsAppTemplate,
  useDeleteWhatsAppTemplate,
  useReplicateWhatsAppTemplate,
  useSyncWhatsAppTemplates,
  useWhatsAppTemplateAccounts,
  useWhatsAppTemplates,
} from '@/hooks/useWhatsAppTemplates';

import { AccountCheckboxList } from './whatsapp-templates/AccountCheckboxList';
import { DeleteTemplateDialog } from './whatsapp-templates/DeleteTemplateDialog';
import { groupTemplates } from './whatsapp-templates/group-templates';
import { TemplateAccountList } from './whatsapp-templates/TemplateAccountList';

import type { TemplateGroup } from './whatsapp-templates/group-templates';
import type {
  AnyComponent,
  CarouselCard,
  CreateWhatsAppTemplateDto,
} from '@/lib/api/whatsapp-templates.api';
import type { ChangeEvent } from 'react';

const EMPTY_CARD: CarouselCard = {
  components: [
    {
      type: 'HEADER',
      format: 'IMAGE',
      example: { header_url: [''] },
    },
    {
      type: 'BODY',
      text: '',
      example: { body_text: [[]] },
    },
    {
      type: 'BUTTONS',
      buttons: [{ type: 'URL', text: 'View Details', url: '', example: [''] }],
    },
  ],
};

const cloneCard = (): CarouselCard => JSON.parse(JSON.stringify(EMPTY_CARD));

type TemplateType = 'text' | 'carousel' | 'json';

interface FormState {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  templateType: TemplateType;
  bodyText: string;
  cards: CarouselCard[];
  jsonText: string;
}

const INITIAL_FORM: FormState = {
  name: '',
  language: 'en_US',
  category: 'UTILITY',
  templateType: 'text',
  bodyText: '',
  cards: [cloneCard(), cloneCard()],
  jsonText: '',
};

const JSON_PLACEHOLDER = `{
  "name": "reengage_showroom_booking",
  "language": "en_GB",
  "category": "MARKETING",
  "components": [
    { "type": "HEADER", "format": "IMAGE", "example": { "header_url": ["https://.../car.jpg"] } },
    { "type": "BODY", "text": "Hi 👋 The {{1}} you enquired about is still available...", "example": { "body_text": [["Mercedes-Benz GLA"]] } },
    { "type": "BUTTONS", "buttons": [
      { "type": "URL", "text": "View vehicle", "url": "https://www.v12sportsandclassics.co.uk/{{1}}", "example": ["used-car-details/.../id-202605052126527/"] },
      { "type": "QUICK_REPLY", "text": "Make showroom booking" }
    ] }
  ]
}`;

const inputClass =
  'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50';

export const WhatsAppTemplatesPage = () => {
  const { data: templates, isLoading } = useWhatsAppTemplates();
  const { data: accounts = [] } = useWhatsAppTemplateAccounts();
  const syncMutation = useSyncWhatsAppTemplates();
  const createMutation = useCreateWhatsAppTemplate();
  const replicateMutation = useReplicateWhatsAppTemplate();
  const deleteMutation = useDeleteWhatsAppTemplate();

  const groups = useMemo(() => groupTemplates(templates ?? [], accounts), [templates, accounts]);

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [deleteGroup, setDeleteGroup] = useState<TemplateGroup | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<FormState>({ ...INITIAL_FORM });
  const [jsonError, setJsonError] = useState('');
  // Accounts the user unchecked in the create dialog; everything is checked by default.
  const [excludedWabaIds, setExcludedWabaIds] = useState<string[]>([]);
  const createWabaIds = accounts
    .map((a) => a.waba_id)
    .filter((id) => !excludedWabaIds.includes(id));

  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleCreateAccount = (wabaId: string, checked: boolean) =>
    setExcludedWabaIds((prev) =>
      checked ? prev.filter((id) => id !== wabaId) : [...prev, wabaId],
    );

  const handleReplicate = (group: TemplateGroup) =>
    replicateMutation.mutate({ id: group.rows[0].id });

  const handleDelete = (group: TemplateGroup, wabaIds: string[]) =>
    deleteMutation.mutate(
      { id: group.rows[0].id, wabaIds },
      { onSuccess: () => setDeleteGroup(null) },
    );

  const updateCard = (cardIndex: number, updater: (card: CarouselCard) => CarouselCard) => {
    setForm((prev) => ({
      ...prev,
      cards: prev.cards.map((c, i) => (i === cardIndex ? updater(c) : c)),
    }));
  };

  const getCardBody = (card: CarouselCard) =>
    card.components.find((c) => c.type === 'BODY')?.text ?? '';

  const getCardImageUrl = (card: CarouselCard) =>
    card.components.find((c) => c.type === 'HEADER')?.example?.header_url?.[0] ?? '';

  const getCardButtonText = (card: CarouselCard) =>
    card.components.find((c) => c.type === 'BUTTONS')?.buttons?.[0]?.text ?? 'View Details';

  const getCardButtonUrl = (card: CarouselCard) =>
    card.components.find((c) => c.type === 'BUTTONS')?.buttons?.[0]?.url ?? '';

  const getCardBodyExample = (card: CarouselCard) =>
    card.components.find((c) => c.type === 'BODY')?.example?.body_text?.[0]?.join(', ') ?? '';

  const getCardButtonExample = (card: CarouselCard) =>
    card.components.find((c) => c.type === 'BUTTONS')?.buttons?.[0]?.example?.[0] ?? '';

  const setCardField = (
    cardIndex: number,
    field: 'body' | 'imageUrl' | 'buttonText' | 'buttonUrl' | 'bodyExample' | 'buttonExample',
    value: string,
  ) => {
    updateCard(cardIndex, (card) => {
      const components = card.components.map((comp) => {
        if (field === 'body' && comp.type === 'BODY') {
          return { ...comp, text: value };
        }
        if (field === 'bodyExample' && comp.type === 'BODY') {
          return {
            ...comp,
            example: { ...comp.example, body_text: [value.split(',').map((s) => s.trim())] },
          };
        }
        if (field === 'imageUrl' && comp.type === 'HEADER') {
          return { ...comp, example: { ...comp.example, header_url: [value] } };
        }
        if (field === 'buttonText' && comp.type === 'BUTTONS' && comp.buttons?.[0]) {
          return {
            ...comp,
            buttons: [{ ...comp.buttons[0], text: value }, ...comp.buttons.slice(1)],
          };
        }
        if (field === 'buttonUrl' && comp.type === 'BUTTONS' && comp.buttons?.[0]) {
          return {
            ...comp,
            buttons: [{ ...comp.buttons[0], url: value }, ...comp.buttons.slice(1)],
          };
        }
        if (field === 'buttonExample' && comp.type === 'BUTTONS' && comp.buttons?.[0]) {
          return {
            ...comp,
            buttons: [{ ...comp.buttons[0], example: [value] }, ...comp.buttons.slice(1)],
          };
        }
        return comp;
      });
      return { components };
    });
  };

  const buildComponents = (): AnyComponent[] => {
    if (form.templateType === 'text') {
      return [{ type: 'BODY', text: form.bodyText }];
    }

    const components: AnyComponent[] = [];
    if (form.bodyText) {
      components.push({ type: 'BODY', text: form.bodyText });
    }
    components.push({ type: 'CAROUSEL', cards: form.cards });
    return components;
  };

  const hasAccountSelected = accounts.length === 0 || createWabaIds.length > 0;
  const isValid =
    hasAccountSelected &&
    (form.templateType === 'json'
      ? !!form.jsonText.trim()
      : form.name &&
        (form.templateType === 'text'
          ? form.bodyText
          : form.cards.length >= 2 && getCardBody(form.cards[0])));

  const handleCreate = () => {
    let payload: CreateWhatsAppTemplateDto;

    if (form.templateType === 'json') {
      try {
        const parsed = JSON.parse(form.jsonText);
        const components = Array.isArray(parsed) ? parsed : parsed.components;
        if (!Array.isArray(components) || components.length === 0) {
          setJsonError('JSON must include a non-empty "components" array.');
          return;
        }
        payload = {
          name: parsed.name || form.name,
          language: parsed.language || form.language,
          category: parsed.category || form.category,
          components,
        };
        setJsonError('');
      } catch (e) {
        setJsonError(e instanceof Error ? e.message : 'Invalid JSON');
        return;
      }
    } else {
      payload = {
        name: form.name,
        language: form.language,
        category: form.category,
        components: buildComponents(),
      };
    }

    // Only narrow the target accounts when the user unchecked something.
    if (excludedWabaIds.length > 0) {
      payload.waba_ids = createWabaIds;
    }

    createMutation.mutate(payload, {
      onSuccess: () => {
        setCreateOpen(false);
        setForm({ ...INITIAL_FORM, cards: [cloneCard(), cloneCard()] });
        setJsonError('');
        setExcludedWabaIds([]);
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">WhatsApp Templates</h1>
          <p className="text-muted-foreground text-sm">
            Manage message templates for WhatsApp Business API
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            {syncMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sync from Meta
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Template
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>Accounts</TableHead>
                <TableHead>Last synced</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                    No templates yet. Create one or sync from Meta.
                  </TableCell>
                </TableRow>
              )}
              {groups.map((group) => {
                const expanded = expandedKeys.has(group.key);
                const replicating =
                  replicateMutation.isPending &&
                  replicateMutation.variables?.id === group.rows[0].id;
                return (
                  <Fragment key={group.key}>
                    <TableRow>
                      <TableCell className="pr-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => toggleExpanded(group.key)}
                          aria-expanded={expanded}
                          aria-label={expanded ? 'Hide accounts' : 'Show accounts'}
                        >
                          {expanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">{group.name}</TableCell>
                      <TableCell>{group.category}</TableCell>
                      <TableCell>{group.language}</TableCell>
                      <TableCell>
                        <Badge variant={group.summaryVariant}>{group.summary}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {new Date(group.lastSyncedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          {group.missingWabaIds.length > 0 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title="Replicate to all accounts"
                              aria-label="Replicate to all accounts"
                              onClick={() => handleReplicate(group)}
                              disabled={replicateMutation.isPending}
                            >
                              {replicating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title="Delete"
                            aria-label="Delete"
                            onClick={() => setDeleteGroup(group)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell />
                        <TableCell colSpan={6} className="bg-muted/30 py-1">
                          <TemplateAccountList accounts={group.accounts} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <DeleteTemplateDialog
        group={deleteGroup}
        isPending={deleteMutation.isPending}
        onConfirm={handleDelete}
        onClose={() => setDeleteGroup(null)}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create WhatsApp Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-1">
              <label className="text-sm font-medium">Template Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. vehicle_search_results"
              />
              <p className="text-muted-foreground text-xs">
                Lowercase, underscores only. Must be unique per language.
              </p>
            </div>

            {/* Category + Language + Type */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Category</label>
                <Select
                  value={form.category}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      category: v as 'MARKETING' | 'UTILITY' | 'AUTHENTICATION',
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTILITY">Utility</SelectItem>
                    <SelectItem value="MARKETING">Marketing</SelectItem>
                    <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Language</label>
                <Input
                  value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  placeholder="en_US"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Type</label>
                <Select
                  value={form.templateType}
                  onValueChange={(v) => setForm({ ...form, templateType: v as TemplateType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="carousel">Carousel</SelectItem>
                    <SelectItem value="json">JSON (advanced)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Accounts to create on */}
            {accounts.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Accounts</label>
                <AccountCheckboxList
                  accounts={accounts}
                  selected={createWabaIds}
                  onToggle={toggleCreateAccount}
                  disabled={createMutation.isPending}
                />
                <p
                  className={
                    hasAccountSelected
                      ? 'text-muted-foreground text-xs'
                      : 'text-destructive text-xs'
                  }
                >
                  {hasAccountSelected
                    ? 'The template is submitted to Meta once per selected WhatsApp Business Account.'
                    : 'Select at least one account.'}
                </p>
              </div>
            )}

            {/* Body text (intro for carousel, full body for text) */}
            {form.templateType !== 'json' && (
              <div className="space-y-1">
                <label className="text-sm font-medium">
                  {form.templateType === 'carousel' ? 'Intro Text' : 'Body Text'}
                </label>
                <textarea
                  className={inputClass}
                  value={form.bodyText}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    setForm({ ...form, bodyText: e.target.value })
                  }
                  placeholder={
                    form.templateType === 'carousel'
                      ? 'Text shown above the carousel (e.g. "Here are the vehicles matching your search:")'
                      : 'Use {{1}}, {{2}} etc. for variable placeholders'
                  }
                  rows={2}
                />
              </div>
            )}

            {/* Raw JSON (advanced) */}
            {form.templateType === 'json' && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Template JSON</label>
                <textarea
                  className={`${inputClass} font-mono text-xs`}
                  value={form.jsonText}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    setForm({ ...form, jsonText: e.target.value })
                  }
                  placeholder={JSON_PLACEHOLDER}
                  rows={16}
                />
                <p className="text-muted-foreground text-xs">
                  Paste the full template. name / language / category are optional here — they fall
                  back to the fields above. Components support HEADER (IMAGE via{' '}
                  <code>example.header_url</code>), BODY, FOOTER and BUTTONS (URL / QUICK_REPLY /
                  PHONE_NUMBER). Images at <code>header_url</code> are uploaded to Meta
                  automatically.
                </p>
                {jsonError && <p className="text-destructive text-xs">{jsonError}</p>}
              </div>
            )}

            {/* Carousel cards */}
            {form.templateType === 'carousel' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Cards ({form.cards.length}/10)</label>
                  {form.cards.length < 10 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setForm({ ...form, cards: [...form.cards, cloneCard()] })}
                    >
                      <Plus className="mr-1 h-3 w-3" /> Add Card
                    </Button>
                  )}
                </div>
                <p className="text-muted-foreground text-xs">
                  All cards share the same structure. Use {'{{1}}'}, {'{{2}}'} for variables. Min 2
                  cards, max 10.
                </p>

                {form.cards.map((card, i) => (
                  <div key={i} className="border-border space-y-2 rounded-lg border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">Card {i + 1}</span>
                      {form.cards.length > 2 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() =>
                            setForm({
                              ...form,
                              cards: form.cards.filter((_, idx) => idx !== i),
                            })
                          }
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>

                    <div className="space-y-1">
                      <label className="text-muted-foreground text-xs">Image URL (example)</label>
                      <Input
                        value={getCardImageUrl(card)}
                        onChange={(e) => setCardField(i, 'imageUrl', e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        className="h-8 text-xs"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-muted-foreground text-xs">
                        Body Text (with variables)
                      </label>
                      <textarea
                        className={`${inputClass} text-xs`}
                        value={getCardBody(card)}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                          setCardField(i, 'body', e.target.value)
                        }
                        placeholder="*{{1}}*&#10;£{{2}} | {{3}} miles&#10;{{4}}"
                        rows={3}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-muted-foreground text-xs">
                        Body Examples (comma-separated, one per variable)
                      </label>
                      <Input
                        value={getCardBodyExample(card)}
                        onChange={(e) => setCardField(i, 'bodyExample', e.target.value)}
                        placeholder="2022 BMW 1 Series, 16995, 18280, Black Diesel Hatchback"
                        className="h-8 text-xs"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="text-muted-foreground text-xs">Button Text</label>
                        <Input
                          value={getCardButtonText(card)}
                          onChange={(e) => setCardField(i, 'buttonText', e.target.value)}
                          placeholder="View Details"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-muted-foreground text-xs">
                          Button URL (with {'{{1}}'} for variable)
                        </label>
                        <Input
                          value={getCardButtonUrl(card)}
                          onChange={(e) => setCardField(i, 'buttonUrl', e.target.value)}
                          placeholder="https://example.com/{{1}}"
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-muted-foreground text-xs">
                        Button URL Example (the {'{{1}}'} value)
                      </label>
                      <Input
                        value={getCardButtonExample(card)}
                        onChange={(e) => setCardField(i, 'buttonExample', e.target.value)}
                        placeholder="used-car-details/example-id"
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!isValid || createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create & Submit to Meta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
